import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import { dataViewI18nT } from '@blocksuite/data-view';
import { WebIcon } from '@blocksuite/icons/lit';
import { cssVarV2 } from '@streamlain/theme/v2';
import { css, html, LitElement, nothing } from 'lit';

/** One app that reads a database, as the viewer may see it. */
export type DatabaseAppUsage = {
  resourceId: string;
  /** Empty when the viewer has no access to the app. */
  title: string;
  ownerName: string;
  stage: 'published' | 'draft';
  state: 'active' | 'trashed';
};

/** A usage read that failed or ran offline is unknown, never "no apps". */
export type DatabaseAppUsageRead =
  | { status: 'ready'; usages: readonly DatabaseAppUsage[] }
  | { status: 'unknown' };

/** Host half: which apps read a database, kept current while watched. */
export interface DatabaseAppUsagePort {
  watch(
    databaseId: string,
    onChange: (read: DatabaseAppUsageRead) => void
  ): () => void;
}

function appTitle(usage: DatabaseAppUsage) {
  return usage.title || dataViewI18nT('database.appUsage.hiddenApp');
}

function usageTags(usage: DatabaseAppUsage) {
  return [
    ...(usage.stage === 'draft'
      ? [dataViewI18nT('database.appUsage.draft')]
      : []),
    ...(usage.state === 'trashed'
      ? [dataViewI18nT('database.appUsage.trashed')]
      : []),
  ];
}

function ownerLine(usage: DatabaseAppUsage) {
  return dataViewI18nT('database.appUsage.owner', { name: usage.ownerName });
}

/** One row of the usage list: app, responsible person, draft and trash. */
export function renderDatabaseAppUsageRow(usage: DatabaseAppUsage) {
  const tags = usageTags(usage);
  return html`<div
    data-testid="database-app-usage-row"
    style="display: flex; flex-direction: column; gap: 2px; min-width: 0;"
  >
    <span
      style="display: flex; align-items: center; gap: 6px; color: ${cssVarV2(
        'text/primary'
      )};"
    >
      <span style="overflow: hidden; text-overflow: ellipsis;"
        >${appTitle(usage)}</span
      >
      ${tags.map(
        tag =>
          html`<span
            data-testid="database-app-usage-tag"
            style="padding: 0 6px; border-radius: 4px; font-size: 12px; line-height: 18px; white-space: nowrap; background: ${cssVarV2(
              'layer/background/tertiary'
            )}; color: ${cssVarV2('text/secondary')};"
            >${tag}</span
          >`
      )}
    </span>
    <span
      style="font-size: 12px; line-height: 18px; color: ${cssVarV2(
        'text/secondary'
      )};"
      >${ownerLine(usage)}</span
    >
  </div>`;
}

/**
 * Highlighted marker at the top left of a database: which apps read it.
 * One app shows its title, several show their count, an unknown read says so.
 */
export class DatabaseAppUsageBadge extends LitElement {
  static override properties = {
    read: { attribute: false },
  };

  static override styles = css`
    :host {
      display: inline-flex;
      flex: 0 0 auto;
      max-width: 240px;
      min-width: 0;
    }

    :host([hidden]) {
      display: none;
    }

    .badge {
      align-items: center;
      background: ${unsafeCSSVarV2('chip/label/blue')};
      border: none;
      border-radius: 4px;
      color: ${unsafeCSSVarV2('text/primary')};
      cursor: pointer;
      display: inline-flex;
      font: inherit;
      font-size: 12px;
      gap: 4px;
      line-height: 18px;
      max-width: 100%;
      min-width: 0;
      padding: 2px 8px;
      user-select: none;
      white-space: nowrap;
    }

    .badge:hover {
      box-shadow: inset 0 0 0 100px
        ${unsafeCSSVarV2('layer/background/hoverOverlay')};
    }

    .badge[data-status='unknown'] {
      background: ${unsafeCSSVarV2('layer/background/tertiary')};
      color: ${unsafeCSSVarV2('text/secondary')};
      cursor: default;
    }

    .badge[data-status='unknown']:hover {
      box-shadow: none;
    }

    .badge svg {
      color: ${unsafeCSSVarV2('icon/primary')};
      flex: 0 0 auto;
      height: 14px;
      width: 14px;
    }

    .label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .tag {
      color: ${unsafeCSSVarV2('text/secondary')};
    }
  `;

  read: DatabaseAppUsageRead | null = null;

  private readonly openList = (event: MouseEvent) => {
    event.stopPropagation();
    const read = this.read;
    if (read?.status !== 'ready' || read.usages.length === 0) return;
    popMenu(popupTargetFromElement(event.currentTarget as HTMLElement), {
      options: {
        items: read.usages.map(usage =>
          menu.action({
            name: appTitle(usage),
            label: () => renderDatabaseAppUsageRow(usage),
            select: () => false,
            testId: 'database-app-usage-item',
          })
        ),
        testId: 'database-app-usage-list',
      },
    });
  };

  private readonly stopPointer = (event: PointerEvent) => {
    event.stopPropagation();
  };

  override render() {
    const read = this.read;
    if (!read || (read.status === 'ready' && read.usages.length === 0)) {
      return nothing;
    }
    if (read.status === 'unknown') {
      const label = dataViewI18nT('database.appUsage.unknown');
      return html`<span
        class="badge"
        data-status="unknown"
        data-testid="database-app-usage-badge"
        title=${label}
        >${WebIcon()}<span class="label">${label}</span></span
      >`;
    }
    const single = read.usages.length === 1 ? read.usages[0] : null;
    const label = single
      ? appTitle(single)
      : dataViewI18nT('database.appUsage.many', { count: read.usages.length });
    const tags = single ? usageTags(single) : [];
    const hint = single ? `${label} · ${ownerLine(single)}` : label;
    return html`<button
      class="badge"
      data-status="ready"
      data-testid="database-app-usage-badge"
      type="button"
      aria-haspopup="menu"
      title=${hint}
      aria-label=${hint}
      @pointerdown=${this.stopPointer}
      @click=${this.openList}
    >
      ${WebIcon()}<span class="label">${label}</span>${tags.map(
        tag => html`<span class="tag">· ${tag}</span>`
      )}
    </button>`;
  }
}

/**
 * Idempotent registration of the badge. The database effects and hosts
 * outside the editor (a dialog before any editor ran) both call it.
 */
export function databaseAppUsageEffects(): void {
  if (!customElements.get('affine-database-app-usage-badge')) {
    customElements.define(
      'affine-database-app-usage-badge',
      DatabaseAppUsageBadge
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'affine-database-app-usage-badge': DatabaseAppUsageBadge;
  }
}
